import { useEffect, useState } from "react";
import CalendarHeader from "src/Components/Calendar/CalendarHeader";
import CalendarGrid from "src/Components/Calendar/CalendarGrid";
import { getWeekNumberInMonth, getMajorityMonthOfWeek, getStartOfWeek } from "src/Common/common";
import WOService from "src/Services/API/wo.service";
import { can, handleError } from "src/Common/function";
import BranchBar from "src/Components/branchbar/BranchBar";
import { useSelector } from "react-redux";
import { useSearchParams, useParams, useNavigate } from "react-router-dom";
import CalendarTabbar, { tabKeyFromPath, pathFromTabKey, visibleCalendarTabs } from "./components/CalendarTabbar";
import CalendarTimeOff from "./timeoff/CalendarTimeOff";
import CalendarTechnicians from "./technicians/CalendarTechnicians";

function Calendar() {
  const today = new Date();
  const [currentDate, setCurrentDate] = useState(today);
  const [view, setView] = useState("Month");
  // header element rendered by the Time Off tab, hoisted into the tabbar row
  const [timeOffHeader, setTimeOffHeader] = useState(null);
  // header element rendered by the Technicians tab, hoisted into the tabbar row
  const [techniciansHeader, setTechniciansHeader] = useState(null);

  const weekStartDate = getStartOfWeek(currentDate);

  // const branch = useSelector((state) => state.Branch.branch);
  const [searchParams] = useSearchParams();
  const branch = Number(searchParams.get("branch"));

  // active tab is driven by the /calendar/:tab route param
  const { tab } = useParams();
  const navigate = useNavigate();
  const activeTab = tabKeyFromPath(tab);

  // Tabs are permission-gated (wo:read / time-off:manage); a gated tab reached
  // by direct URL falls back to the first tab the user can see. With no
  // visible tab at all (the side menu hides Calendar then) nothing renders.
  const canReadWO = can("wo:read");
  const canManageTimeOff = can("time-off:manage");
  const visibleTabs = visibleCalendarTabs();
  const activeTabVisible = visibleTabs.some((t) => t.key === activeTab);
  useEffect(() => {
    if (!activeTabVisible && visibleTabs.length > 0) {
      const search = searchParams.toString();
      navigate(`/calendar/${visibleTabs[0].path}${search ? `?${search}` : ""}`, { replace: true });
    }
  }, [activeTabVisible, visibleTabs, navigate, searchParams]);

  const handleTabChange = (key) => {
    const search = searchParams.toString();
    navigate(`/calendar/${pathFromTabKey(key)}${search ? `?${search}` : ""}`);
  };

  // Determine majority month/week for header and week number
  const { year, month } =
    view === "Week"
      ? getMajorityMonthOfWeek(weekStartDate)
      : {
          year: currentDate.getFullYear(),
          month: currentDate.getMonth(),
        };

  const monthName = new Date(year, month).toLocaleDateString("default", {
    month: "long",
  });

  const weekNumber = view === "Week" ? getWeekNumberInMonth(weekStartDate, month, year) : null;

  const [workOrders, setWorkOrders] = useState([]);

  useEffect(() => {
    if (!canReadWO) return;
    WOService.getWOsForCalendar(view, year, month, weekNumber, branch)
      .then((response) => {
        if (response.status == 200) {
          console.log("workorder ==> ", response.data);
          setWorkOrders(response.data);
        }
      })
      .catch((error) => {
        handleError(error);
      });
  }, [month, year, weekNumber, branch, canReadWO]);

  const handlePrev = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(view === "Month" ? 1 : newDate.getDate() - 7);
    if (view === "Month") {
      newDate.setMonth(currentDate.getMonth() - 1);
    }
    setCurrentDate(newDate);
  };

  const handleNext = () => {
    const newDate = new Date(currentDate);
    newDate.setDate(view === "Month" ? 1 : newDate.getDate() + 7);
    if (view === "Month") {
      newDate.setMonth(currentDate.getMonth() + 1);
    }
    setCurrentDate(newDate);
  };

  const handleView = (selectedView) => {
    setView(selectedView);
  };

  return (
    <div className="w-full h-full">
      <div className="px-5 py-2 border-b border-borderColor">
        <BranchBar allEnabled={false} />
      </div>
      <div
        className="px-5 flex items-center justify-between mt-3 relative"
        style={{ borderBottom: "1px solid var(--Gray-200, #E5E8F3)" }}
      >
        <CalendarTabbar activeTab={activeTab} onChange={handleTabChange} />
        {activeTab === "workOrders" && canReadWO && (
          <CalendarHeader
            month={monthName}
            year={year}
            week={weekNumber}
            view={view}
            onPrev={handlePrev}
            onNext={handleNext}
            onView={handleView}
          />
        )}
        {activeTab === "timeOff" && canManageTimeOff && timeOffHeader}
        {activeTab === "technicians" && canManageTimeOff && techniciansHeader}
      </div>

      <div className="px-5">
        {activeTab === "workOrders" && canReadWO && (
          <CalendarGrid month={month} year={year} week={weekNumber} view={view} workOrders={workOrders} />
        )}

        {activeTab === "technicians" && canManageTimeOff && (
          <CalendarTechnicians renderHeader={setTechniciansHeader} branch={branch} />
        )}

        {activeTab === "timeOff" && canManageTimeOff && (
          <CalendarTimeOff renderHeader={setTimeOffHeader} branch={branch} />
        )}
      </div>
    </div>
  );
}

export default Calendar;
